import React from "react";
import { getTheme } from "./studyGroupConfig";

const StudyGroupCard = ({
  group,
  openMenuId,
  setOpenMenuId,
  menuRef,
  onManageGroup,
  onEditGroup,
  onQuickCopyCode,
  onDeleteGroup,
  onLeaveGroup,
  onEnterHub,
}) => {
  const theme = getTheme(group.color);
  const isOwner = !group.isShared;
  const isFull = group.members >= group.maxMembers;
  const spotsLeft = Math.max(0, group.maxMembers - group.members);
  const fillPercent = Math.min(
    100,
    Math.round((group.members / group.maxMembers) * 100)
  );

  return (
    <article
      className={`group relative bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border ${theme.border} p-5 flex flex-col justify-between ${
        openMenuId === group.id ? "z-30 overflow-visible" : "z-10 overflow-hidden"
      } border-t-4 hover:-translate-y-1`}
    >
      {/* Soft ambient corner decorative accent */}
      <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
        <div
          className={`absolute -right-6 -top-6 w-24 h-24 ${theme.dot} rounded-full transition-transform duration-500 group-hover:scale-125 opacity-60`}
        />
      </div>

      {/* Top Row: Icon, Role Pill & Menu */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3 relative z-10">
          <div
            className={`w-10 h-10 rounded-xl ${theme.iconBg} ${theme.iconText} flex items-center justify-center text-lg shrink-0 shadow-xs`}
          >
            <i className="fa-solid fa-users"></i>
          </div>

          {/* Actions & Role Indicator */}
          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            {isOwner ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200/60 flex items-center gap-1">
                <i className="fa-solid fa-crown text-[9px]"></i> Owner
              </span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200/60 flex items-center gap-1">
                <i className="fa-solid fa-handshake text-[9px]"></i> Joined
              </span>
            )}

            {Number(group.unreadMessagesCount || 0) > 0 && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200/80 flex items-center gap-1 animate-pulse"
                title={`${group.unreadMessagesCount} unread message(s)`}
              >
                <i className="fa-solid fa-comment-dots text-[9px]"></i>
                <span>{group.unreadMessagesCount} new</span>
              </span>
            )}

            {/* Dropdown Menu Container */}
            <div
              className="relative"
              ref={openMenuId === group.id ? menuRef : null}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId((prev) => (prev === group.id ? null : group.id));
                }}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition relative ${
                  openMenuId === group.id ? "bg-gray-100 text-gray-700" : ""
                }`}
                title="Group options"
              >
                <i className="fa-solid fa-ellipsis-vertical text-xs"></i>
                {isOwner && Number(group.pendingRequestsCount || 0) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse"></span>
                )}
              </button>

              {/* Anchored Floating Dropdown Menu */}
              {openMenuId === group.id && (
                <div className="absolute right-0 top-8 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 p-1.5 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                  {isOwner ? (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onManageGroup(group);
                        }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl flex items-center justify-between transition"
                      >
                        <div className="flex items-center gap-2">
                          <i className="fa-solid fa-sliders text-xs w-4 text-center"></i>
                          <span>Manage Group</span>
                        </div>
                        {Number(group.pendingRequestsCount || 0) > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold shrink-0 animate-pulse">
                            {group.pendingRequestsCount}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditGroup(group);
                        }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl flex items-center gap-2 transition"
                      >
                        <i className="fa-solid fa-pen text-xs w-4 text-center text-gray-400"></i>
                        <span>Edit Details</span>
                      </button>
                      <button
                        onClick={(e) => onQuickCopyCode(group, e)}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl flex items-center gap-2 transition"
                      >
                        <i className="fa-solid fa-copy text-xs w-4 text-center text-gray-400"></i>
                        <span>Copy Invite Code</span>
                      </button>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteGroup(group);
                        }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-2 transition"
                      >
                        <i className="fa-solid fa-trash text-xs w-4 text-center text-red-500"></i>
                        <span>Delete Group</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={(e) => onQuickCopyCode(group, e)}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-xl flex items-center gap-2 transition"
                      >
                        <i className="fa-solid fa-copy text-xs w-4 text-center text-gray-400"></i>
                        <span>Copy Invite Code</span>
                      </button>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onLeaveGroup(group);
                        }}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-2 transition"
                      >
                        <i className="fa-solid fa-right-from-bracket text-xs w-4 text-center text-red-500"></i>
                        <span>Leave Group</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subject Tag, Group Title & Schedule Metadata */}
        <div className="mb-4">
          {group.subject && (
            <div className="mb-1.5">
              <span
                className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-md border truncate max-w-full ${theme.badgeBg}`}
                title={group.subject}
              >
                {group.subject}
              </span>
            </div>
          )}
          <h3
            className="font-bold text-gray-900 text-base leading-snug line-clamp-1 mb-1 group-hover:text-blue-600 transition"
            title={group.name}
          >
            {group.name}
          </h3>
          <p className="text-[11px] text-gray-400 font-medium flex items-center gap-1.5">
            <i className="fa-regular fa-calendar text-[10px]"></i>
            <span>Created {group.schedule}</span>
          </p>
        </div>
      </div>

      {/* Bottom Section: Capacity Bar, Invite Code Chip & Enter Button */}
      <div className="space-y-3 pt-2">
        {/* Capacity Progress Meter */}
        <div>
          <div className="flex items-center justify-between text-[11px] mb-1.5">
            <span className="font-semibold text-gray-500 flex items-center gap-1">
              <i className="fa-solid fa-user-check text-[10px] text-gray-400"></i>
              <span>Capacity</span>
            </span>
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-gray-800">
                {group.members}/{group.maxMembers}
              </span>
              {isFull ? (
                <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-rose-50 text-rose-600">
                  FULL
                </span>
              ) : spotsLeft <= 2 ? (
                <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-orange-50 text-orange-600">
                  {spotsLeft} LEFT
                </span>
              ) : (
                <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-600">
                  OPEN
                </span>
              )}
            </div>
          </div>
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${theme.progress} transition-all duration-500 rounded-full`}
              style={{ width: `${fillPercent}%` }}
            />
          </div>
        </div>

        {/* Quick Invite Code Snippet (if active) */}
        {group.inviteCode && group.inviteCodeActive && (
          <div className="flex items-center justify-between px-2.5 py-1.5 rounded-xl bg-gray-50 border border-gray-100 text-[11px]">
            <span className="font-mono font-bold text-gray-600 tracking-wider">
              {group.inviteCode}
            </span>
            <button
              type="button"
              onClick={(e) => onQuickCopyCode(group, e)}
              className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 transition"
              title="Copy invite code"
            >
              <i className="fa-solid fa-copy text-[10px]"></i>
              <span className="text-[10px]">Copy</span>
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-1">
          {isOwner && (
            <button
              type="button"
              onClick={() => onManageGroup(group)}
              className="px-3 py-2 rounded-xl text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 hover:text-gray-900 transition flex items-center justify-center shrink-0"
              title="Manage members & settings"
            >
              <i className="fa-solid fa-gear"></i>
            </button>
          )}

          <button
            type="button"
            onClick={() => onEnterHub(group.id)}
            className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 shadow-sm ${theme.btn} group-hover:shadow relative`}
          >
            <span>Enter Hub</span>
            <i className="fa-solid fa-arrow-right text-[10px] transition-transform group-hover:translate-x-1"></i>
            {Number(group.unreadMessagesCount || 0) > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 px-1.5 py-0.2 rounded-full bg-blue-600 text-white text-[10px] font-bold ring-2 ring-white animate-pulse"
                title={`${group.unreadMessagesCount} unread message(s)`}
              >
                {group.unreadMessagesCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </article>
  );
};

export default StudyGroupCard;
